import React, { useEffect, useState } from 'react';
import { dataforseoService } from '../../lib/dataforseoService';
import { RankedKeywordData } from '../../lib/types';
import { supabase } from '../../lib/supabase';
import { Loader, AlertTriangle } from 'lucide-react';

const TestKeywordsComponent: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [prospectData, setProspectData] = useState<any>(null);
  const [domain, setDomain] = useState<string>('');
  const [keywords, setKeywords] = useState<RankedKeywordData[] | null>(null);
  const [storedData, setStoredData] = useState<any>(null);
  const [logs, setLogs] = useState<string[]>([]);

  const prospectId = 'c40d9723-8871-41be-b5a8-5fbbd3e444f4';

  const addLog = (message: string) => {
    setLogs(prev => [...prev, `[${new Date().toLocaleTimeString()}] ${message}`]);
  };

  useEffect(() => {
    const testKeywords = async () => {
      try {
        setLoading(true);
        addLog(`Récupération des informations pour le prospect ${prospectId}...`);
        
        // 1. Récupérer l'URL du site du prospect
        const { data, error: prospectError } = await supabase
          .from('prospects')
          .select('site_url, prenom, nom, metadata')
          .eq('id', prospectId)
          .maybeSingle();
          
        if (prospectError) {
          addLog(`Erreur lors de la récupération des données du prospect: ${prospectError.message}`);
          setError(`Erreur lors de la récupération des données du prospect: ${prospectError.message}`);
          return;
        }
        
        if (!data || !data.site_url) {
          const errorMsg = 'Prospect non trouvé ou URL du site manquante';
          addLog(errorMsg);
          setError(errorMsg);
          return;
        }
        
        setProspectData(data);
        const companyName = data.metadata?.company || 'Non spécifié';
        const fullName = `${data.prenom || ''} ${data.nom || ''}`.trim();
        addLog(`Prospect trouvé: ${fullName}, Entreprise: ${companyName}, URL: ${data.site_url}`);
        
        // 2. Extraire le domaine de l'URL
        let extractedDomain = data.site_url;
        try {
          if (!extractedDomain.startsWith('http://') && !extractedDomain.startsWith('https://')) {
            extractedDomain = 'https://' + extractedDomain;
          }
          const urlObj = new URL(extractedDomain);
          extractedDomain = urlObj.hostname;
          if (extractedDomain.startsWith('www.')) {
            extractedDomain = extractedDomain.substring(4);
          }
          setDomain(extractedDomain);
          addLog(`Domaine extrait: ${extractedDomain}`);
        } catch (e) {
          addLog(`Erreur lors de l'extraction du domaine: ${e instanceof Error ? e.message : String(e)}`);
          setDomain(data.site_url);
        }
        
        // 4. Vérifier d'abord les données stockées dans Supabase
        addLog(`Vérification des données stockées dans Supabase...`);
        const { data: stored, error: storedError } = await supabase
          .from('keyword_analysis_results')
          .select('*')
          .eq('prospect_id', prospectId)
          .maybeSingle();
          
        if (storedError) {
          addLog(`Erreur lors de la récupération des données stockées: ${storedError.message}`);
        } else if (!stored) {
          addLog('Aucune donnée stockée trouvée pour ce prospect.');
        } else {
          addLog(`Données stockées trouvées: ${stored.ranked_keywords?.length || 0} mots-clés, analysés le ${new Date(stored.analyzed_at).toLocaleString()}`);
          setStoredData(stored);
          
          // Utiliser les données stockées si elles existent
          if (stored.ranked_keywords && stored.ranked_keywords.length > 0) {
            addLog(`Utilisation des ${stored.ranked_keywords.length} mots-clés stockés dans Supabase.`);
            setKeywords(stored.ranked_keywords);
          }
        }
        
        // 3. Récupérer les mots-clés classés depuis l'API si nécessaire
        if (!keywords || keywords.length === 0) {
          addLog(`Récupération des mots-clés classés depuis l'API pour ${extractedDomain}...`);
          const keywordsData = await dataforseoService.analyzeDomain(extractedDomain, prospectId, true);
          
          // Convertir KeywordData en RankedKeywordData pour l'affichage
          const rankedKeywordsData = keywordsData?.map(kw => ({
            keyword: kw.keyword,
            rank_group: kw.position,
            rank_absolute: kw.position,
            url: kw.url || `https://${extractedDomain}`,
            search_volume: kw.search_volume || 0,
            cpc: 0,
            competition: 0,
            etv: 0,
            estimated_paid_traffic_cost: 0,
            title: '',
            description: ''
          }));
          
          if (!rankedKeywordsData || rankedKeywordsData.length === 0) {
            addLog('Aucun mot-clé classé trouvé depuis l\'API.');
          } else {
            addLog(`${rankedKeywordsData.length} mots-clés classés trouvés depuis l'API.`);
            setKeywords(rankedKeywordsData);
          }
        }
      } catch (err) {
        const errorMsg = `Erreur lors du test: ${err instanceof Error ? err.message : String(err)}`;
        addLog(errorMsg);
        setError(errorMsg);
      } finally {
        setLoading(false);
      }
    };

    testKeywords();
  }, []);

  return (
    <div className="bg-white rounded-lg shadow-md p-6 max-w-5xl mx-auto my-8">
      <h2 className="text-2xl font-bold mb-6">Test des mots-clés classés pour le prospect</h2>
      
      {loading && (
        <div className="flex items-center justify-center p-8">
          <Loader className="w-8 h-8 text-blue-500 animate-spin mr-3" />
          <span className="text-gray-600">Chargement en cours...</span>
        </div>
      )}
      
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-md p-4 mb-6">
          <div className="flex">
            <AlertTriangle className="w-5 h-5 text-red-500 mr-2" />
            <span className="text-red-700">{error}</span>
          </div>
        </div>
      )}
      
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div>
          <h3 className="text-lg font-semibold mb-3">Informations du prospect</h3>
          {prospectData && (
            <div className="bg-gray-50 rounded-md p-4">
              <p><span className="font-medium">ID:</span> {prospectId}</p>
              <p><span className="font-medium">Nom:</span> {`${prospectData.prenom || ''} ${prospectData.nom || ''}`.trim()}</p>
              <p><span className="font-medium">Entreprise:</span> {prospectData.metadata?.company || 'Non spécifié'}</p>
              <p><span className="font-medium">URL du site:</span> {prospectData.site_url}</p>
              <p><span className="font-medium">Domaine extrait:</span> {domain}</p>
            </div>
          )}
          
          <h3 className="text-lg font-semibold mt-6 mb-3">Logs d'exécution</h3>
          <div className="bg-gray-800 text-gray-100 rounded-md p-4 font-mono text-sm h-64 overflow-y-auto">
            {logs.map((log, index) => (
              <div key={index} className="mb-1">{log}</div>
            ))}
          </div>
        </div>
        
        <div>
          <h3 className="text-lg font-semibold mb-3">Mots-clés classés trouvés</h3>
          {keywords && keywords.length > 0 ? (
            <div className="bg-gray-50 rounded-md overflow-hidden">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-100">
                  <tr>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Mot-clé</th>
                    <th className="px-4 py-2 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">Rang</th>
                    <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Volume</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {keywords.slice(0, 10).map((keyword, index) => (
                    <tr key={index} className="hover:bg-gray-50">
                      <td className="px-4 py-2 text-sm text-gray-900 truncate max-w-xs" title={keyword.keyword}>
                        {keyword.keyword}
                      </td>
                      <td className="px-4 py-2 text-sm text-center">
                        <span
                          className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                            keyword.rank_group <= 3 ? 'bg-green-100 text-green-800' :
                            keyword.rank_group <= 10 ? 'bg-blue-100 text-blue-800' :
                            'bg-gray-100 text-gray-800'
                          }`}
                        >
                          {keyword.rank_group}
                        </span>
                      </td>
                      <td className="px-4 py-2 text-sm text-gray-500 text-right">
                        {keyword.search_volume ? keyword.search_volume.toLocaleString() : '-'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {keywords.length > 10 && (
                <div className="bg-gray-50 px-4 py-2 text-sm text-gray-500 text-right border-t">
                  Affichage de 10 mots-clés sur {keywords.length}
                </div>
              )}
            </div>
          ) : (
            <div className="bg-gray-50 rounded-md p-8 text-center text-gray-500">
              Aucun mot-clé classé trouvé pour ce domaine.
            </div>
          )}
          
          <h3 className="text-lg font-semibold mt-6 mb-3">Données stockées dans Supabase</h3>
          {storedData ? (
            <div className="bg-gray-50 rounded-md p-4">
              <p><span className="font-medium">Domaine:</span> {storedData.domain}</p>
              <p><span className="font-medium">Type d'analyse:</span> {storedData.analysis_type}</p>
              <p><span className="font-medium">Nombre de mots-clés:</span> {storedData.ranked_keywords?.length || 0}</p>
              <p><span className="font-medium">Date d'analyse:</span> {new Date(storedData.analyzed_at).toLocaleString()}</p>
              
              {storedData.ranked_keywords && storedData.ranked_keywords.length > 0 && (
                <div className="mt-4">
                  <p className="font-medium mb-2">Premier mot-clé stocké:</p>
                  <div className="bg-white p-3 rounded border border-gray-200">
                    <p><span className="font-medium">Mot-clé:</span> {storedData.ranked_keywords[0].keyword}</p>
                    <p><span className="font-medium">Rang:</span> {storedData.ranked_keywords[0].rank_group}</p>
                    <p><span className="font-medium">Volume:</span> {storedData.ranked_keywords[0].search_volume}</p>
                  </div>
                </div>
              )}
            </div>
          ) : (
            <div className="bg-gray-50 rounded-md p-8 text-center text-gray-500">
              Aucune donnée stockée trouvée pour ce prospect.
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default TestKeywordsComponent;
